import { useFlags } from "launchdarkly-react-client-sdk";
import { useParams } from "react-router-dom";

import KeyIcon from "@mui/icons-material/Key";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { ENABLE_SSO_INVITATIONS } from "constants/feature-flags";
import { startSso } from "utils/sso.util";

// Public landing for the emailed invite link (/accept/:token). The invitee isn't
// logged in yet, and the backend exposes NO public invite-context endpoint
// (InviteViewSet is admin-only), so we show generic copy + a single SSO button
// carrying the invite token. The backend redeems the token and is the authority
// on validity — a bad token comes back as /sso/error#sso_error=invite_invalid.
// Layout: UnauthorizedLayout provides the Suno logo + centering; this is the card.
export default function InviteAccept() {
  const { token = "" } = useParams();
  const flags = useFlags();
  const isSsoEnabled = flags[ENABLE_SSO_INVITATIONS] ?? false;

  if (!isSsoEnabled) {
    return (
      <Alert icon={false} severity="info" sx={{ width: "100%" }}>
        SSO invitations aren&apos;t available right now. Please contact your
        administrator.
      </Alert>
    );
  }

  if (!token) {
    return (
      <Alert icon={false} severity="error" sx={{ width: "100%" }}>
        This invitation link is invalid or incomplete. Please ask your
        administrator to resend it.
      </Alert>
    );
  }

  return (
    <Stack gap={3} sx={{ width: "100%" }} alignItems="center">
      <Stack gap={1} alignItems="center">
        <Typography variant="h4" fontWeight={700} textAlign="center">
          You&apos;ve been invited to Suno
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Sign in with single sign-on to finish setting up your account.
        </Typography>
      </Stack>

      <Button
        type="button"
        variant="contained"
        size="large"
        fullWidth
        startIcon={<KeyIcon />}
        onClick={() => startSso(token)}
        data-testid="sso-sign-in"
      >
        Sign in with SSO
      </Button>
    </Stack>
  );
}
